/**
 * ScopedAccessGuard.jsx
 *
 * When a user has a restricted `access_scope` (e.g. "football_preview"
 * for Glenn), every route that isn't on their allow-list redirects
 * to their designated home. This is a hard lock — Glenn cannot poke
 * around the Atelier or Team Alignment areas by URL guessing.
 *
 * The guard wraps every ProtectedRoute child. It sits between the
 * auth check and the actual page, so the flow is:
 *   ProtectedRoute → user loaded → ScopedAccessGuard → page
 *
 * Add new allowed paths per scope in ALLOW_LISTS below.
 */
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

// Per-scope allow-list. A route is allowed if any prefix matches.
// Always include the auth/account routes so the user can log out.
const ACCOUNT = ["/account", "/login", "/logout"];
const ALLOW_LISTS = {
  football_preview: [
    "/football",
    "/golf",                   // Reclaim Golf performance system (added Feb 2026)
    "/reclaim-performance/golf",
    "/practices",              // Resets + State Tuning + Tapping live here
    "/daily-essentials",       // Day + Night rituals
    "/account",
    "/login",
  ],
  // Studio testers: one unfinished area each (Oct 2026).
  golf_tester: ["/golf", "/reclaim-performance/golf", "/practices", "/daily-essentials", ...ACCOUNT],
  networker_tester: ["/networker", ...ACCOUNT],
  inner_path_tester: ["/inner-path", ...ACCOUNT],
  gym_tester: ["/gym", ...ACCOUNT],
  atelier_tester: ["/atelier", ...ACCOUNT],
  stand_easy_tester: ["/stand-easy", "/practices", "/networker/resets", ...ACCOUNT],
};

// The landing page each scope should be redirected to.
export const SCOPE_HOME = {
  football_preview: "/football",
  golf_tester: "/golf",
  networker_tester: "/networker",
  inner_path_tester: "/inner-path",
  gym_tester: "/gym",
  atelier_tester: "/atelier",
  stand_easy_tester: "/stand-easy",
};

// Studio lock: the finished, public products. Every other signed-in page is
// unfinished Reclaim work, visible only to admins and studio testers.
const PUBLIC_OPEN = [
  "/soulumo", "/my-library", "/my-installations", "/my-journeys", "/signature-installations",
  "/voice-library", "/voice-clone", "/foundations", "/one-million-lights", "/lights",
  "/share", "/gift", "/gift-moment", "/with", "/upgrade", "/payment", "/together/new", ...ACCOUNT,
  "/golf/deep", "/golf/coach", "/gym/deep", "/gym/guide",
];
// Specialist membership landings (tracks inside are gated by AreaGate / the Coach allowance).
const PUBLIC_EXACT = ["/golf", "/gym", "/golf/deeper-work"];

function isAllowed(pathname, allowList) {
  return allowList.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

export const isStudioUser = (u) => !!u && (u.role === "admin" || !!u.access_scope);
export const studioHome = (u) => (u?.role === "admin" || u?.access_scope === "studio_all" ? "/reclaim-home" : SCOPE_HOME[u?.access_scope] || "/reclaim-home");

export default function ScopedAccessGuard({ children }) {
  const { user } = useAuth();
  const location = useLocation();

  // Oct 2026 · restricted_journeys — a per-user whitelist of journey
  // slugs. When set with a single slug (e.g. ["uk-driving-theory"]),
  // the user's home becomes that journey's landing page and every
  // other route redirects there. Admins never enter this branch.
  const restricted = user?.restricted_journeys;
  if (restricted && restricted.length > 0 && user?.role !== "admin") {
    const slug = restricted[0];
    const journeysAllow = [
      "/journeys",             // list + /journeys/:slug + /journeys/:slug/day/:n
      "/journey",              // older alias if present
      "/account",
      "/login",
      "/logout",
    ];
    if (journeysAllow.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"))) {
      // Still inside the allowed shell — but if they're trying to open
      // a different journey, block it (backend will 403 anyway).
      const otherJourney = location.pathname.match(/^\/journeys\/([^/]+)/);
      if (otherJourney && otherJourney[1] !== slug) {
        return <Navigate to={`/journeys/${slug}`} replace />;
      }
      return children;
    }
    return <Navigate to={`/journeys/${slug}`} replace />;
  }

  const scope = user?.access_scope;
  if (user?.role === "admin" || scope === "studio_all") return children;
  if (scope && ALLOW_LISTS[scope]) {
    if (isAllowed(location.pathname, ALLOW_LISTS[scope]) || isAllowed(location.pathname, PUBLIC_OPEN) || PUBLIC_EXACT.includes(location.pathname)) return children;
    return <Navigate to={SCOPE_HOME[scope] || "/"} replace />;
  }
  if (isAllowed(location.pathname, PUBLIC_OPEN) || PUBLIC_EXACT.includes(location.pathname)) return children;
  return <Navigate to="/" replace />;
}
